class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.sasActiveRule = null;
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    const { clear, popularSearchItems } = this.domNodes;
    const form = this.querySelector("form.m-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    clear.addEventListener("click", this.onClearSearch.bind(this));
    popularSearchItems.forEach((item) => item.addEventListener("click", this.onSasPopularSearchClick.bind(this)));
    this.predictiveSearchResults.addEventListener("click", this.onSasPredictiveResultClick.bind(this));
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
  }

  getSasRuleMatch(term) {
    return (window.SasSearchTermRules && window.SasSearchTermRules.findMatch(term)) || null;
  }

  handleSasRedirect(rule, term, context, options) {
    if (window.SasSearchTermRules) window.SasSearchTermRules.trackMatch(rule, term, context);

    if (!window.SasSearchTermRules || !window.SasSearchTermRules.isValidUrl(rule.url)) {
      console.warn("[sas-search-term-rules] Ignoring redirect rule with invalid/missing URL:", rule.url);
      this.renderSasRuleMessage(rule);
      return;
    }

    window.SasSearchTermRules.applyRedirect(rule, options);
  }

  onSasPredictiveResultClick(event) {
    const link = event.target.closest("a");
    if (!link) return;

    const linkText = (link.textContent || "").trim();
    const sasTerm = linkText || this.getQuery();
    const sasRule = this.getSasRuleMatch(linkText) || this.getSasRuleMatch(this.getQuery());
    if (!sasRule) return;

    event.preventDefault();
    if (sasRule.action === "redirect") {
      this.handleSasRedirect(sasRule, sasTerm, "suggestion_click", { allowNewTab: true });
    } else {
      if (window.SasSearchTermRules) window.SasSearchTermRules.trackMatch(sasRule, sasTerm, "suggestion_click");
      this.renderSasRuleMessage(sasRule);
    }
  }

  renderSasRuleMessage(rule) {
    this.sasActiveRule = rule;
    this.predictiveSearchResults.innerHTML = "";
    this.setAttribute("results", true);
    this.renderSearchQueryAndMessage(false);
    const sasDisplayMessage = window.SasSearchTermRules ? window.SasSearchTermRules.getDisplayMessage(rule) : rule.message;
    if (sasDisplayMessage) this.domNodes.searchMessage.textContent = sasDisplayMessage;
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  onSasPopularSearchClick(event) {
    const term = event.currentTarget.dataset.psQuery;
    const sasRule = this.getSasRuleMatch(term);
    if (!sasRule) return;

    event.preventDefault();
    if (sasRule.action === "redirect") {
      this.handleSasRedirect(sasRule, term, "popular_search_click", { allowNewTab: true });
    } else if (window.SasSearchTermRules) {
      window.SasSearchTermRules.trackMatch(sasRule, term, "popular_search_click");
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    // Falls back to the rule currently shown in the dropdown (sasActiveRule)
    // because the raw input can still be a partial term (e.g. "tag heu")
    // that only matched via a rendered suggestion's full text ("TAG Heuer"),
    // not via the literal characters typed so far.
    const sasRule = this.getSasRuleMatch(this.getQuery()) || this.sasActiveRule;
    if (sasRule) {
      const sasIsMessageClick = event.submitter === this.searchCount;
      const sasContext = sasIsMessageClick ? "suggestion_click" : "submit";
      if (sasRule.action === "redirect") {
        event.preventDefault();
        this.handleSasRedirect(sasRule, this.getQuery(), sasContext, { allowNewTab: sasIsMessageClick });
      } else if (window.SasSearchTermRules) {
        window.SasSearchTermRules.trackMatch(sasRule, this.getQuery(), sasContext);
      }
      return;
    }

    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const sasRule = this.getSasRuleMatch(searchTerm);
    if (sasRule) {
      if (window.SasSearchTermRules) window.SasSearchTermRules.trackMatch(sasRule, searchTerm, "typing");
      this.renderSasRuleMessage(sasRule);
      return;
    }

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption;

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSasCheckedResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    let searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(`${searchURL}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSasCheckedResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  getSasMarkupRuleMatch(markup) {
    if (!markup) return null;

    const wrapper = document.createElement("div");
    wrapper.innerHTML = markup;

    const links = wrapper.querySelectorAll("a");
    for (const link of links) {
      const sasRule = this.getSasRuleMatch((link.textContent || "").trim());
      if (sasRule) return sasRule;
    }

    return null;
  }

  renderSasCheckedResults(resultsMarkup, searchTerm) {
    const sasRule = this.getSasMarkupRuleMatch(resultsMarkup);
    if (sasRule) {
      if (window.SasSearchTermRules) window.SasSearchTermRules.trackMatch(sasRule, searchTerm, "typing_suggestion");
      this.renderSasRuleMessage(sasRule);
      return;
    }

    this.renderSearchResults(resultsMarkup);
  }

  renderSearchResults(resultsMarkup) {
    this.sasActiveRule = null;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    if (searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const query = input.value;

    const { resultsTitle } = searchMessage.dataset;
    searchQuery.textContent = query;

    this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupSearults.classList.remove("m:hidden");

    if (results) {
      searchMessage.textContent = resultsTitle;
      moreResultIcon.classList.add("m:hidden");
    } else {
      searchMessage.textContent = searchMessage.dataset.resultsTitle;
      moreResultIcon.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    submit.style.visibility = show ? "hidden" : "visible";
    loading.style.visibility = show ? "visible" : "hidden";
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    this.popularSearches && this.popularSearches.classList.add("m:hidden");
    this.searchCount.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.sasActiveRule = null;
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    this.predictiveSearchResults.classList.add("m:hidden");
    this.searchPopupSearults.classList.add("m:hidden");
    this.popularSearches && this.popularSearches.classList.remove("m:hidden");
    this.searchCount.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector("predictive-search").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);
